import { Card, CardContent } from "@/components/ui/card";
import { ScrollArea, ScrollBar } from "@/components/ui/scroll-area";
import { Button } from "@/components/ui/button";
import { Info, MessageSquare, Trash2, Reply, Phone, Mail, MapPin, Calendar, Clock } from "lucide-react";
import { format } from "date-fns";
import { fr } from "date-fns/locale";
import { Patient } from "@/types/patient.types";
import { usePatientData } from "../hooks/usePatientData";
import { useToast } from "@/hooks/use-toast";
import { MessageDialog } from "@/components/services/MessageDialog";
import { useState } from "react";
import { PatientHealthData } from "@/components/professional/dashboard/patient/PatientHealthData";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/use-auth";
import { cn } from "@/lib/utils";
import { useIsMobile } from "@/hooks/use-mobile";
import { EnhancedPatientDetails } from "./EnhancedPatientDetails";

interface PatientDetailsProps {
  relation: Patient;
  messages: any[];
  isLoadingMessages: boolean;
}

export const PatientDetails = ({ relation, messages, isLoadingMessages }: PatientDetailsProps) => {
  const { mutate: deletePatientData } = usePatientData();
  const { toast } = useToast();
  const [replyTo, setReplyTo] = useState<string | null>(null);
  const { session } = useAuth();
  const isMobile = useIsMobile();

  const { data: accessData, isLoading: isLoadingAccess } = useQuery({
    queryKey: ["patient-access", relation.patient?.id],
    queryFn: async () => {
      if (!session?.user?.id || !relation.patient?.id) return { hasAccess: false };
      
      const { data, error } = await supabase
        .from("professional_patient_access")
        .select("*")
        .eq("professional_id", session.user.id)
        .eq("patient_id", relation.patient.id)
        .eq("status", "approved")
        .maybeSingle();
        
      if (error || !data) return { hasAccess: false };
      return { 
        hasAccess: true,
        ...data
      };
    },
    enabled: !!session?.user?.id && !!relation.patient?.id,
  });

  const { data: professionalProfile } = useQuery({
    queryKey: ["professionalProfile"],
    queryFn: async () => {
      if (!session?.user) return null;
      
      const { data, error } = await supabase
        .from("profiles")
        .select("company_name")
        .eq("id", session.user.id)
        .single();
        
      if (error) throw error;
      return data;
    },
    enabled: !!session?.user,
  });

  const handleDeletePatient = async () => {
    try {
      await deletePatientData(relation.patient?.id as string);
      toast({
        title: "Patient supprimé",
        description: "Les données du patient ont été supprimées avec succès",
      });
    } catch (error) {
      toast({
        variant: "destructive",
        title: "Erreur",
        description: "Impossible de supprimer les données du patient",
      });
    }
  };

  const hasAccess = !!accessData?.hasAccess;

  return (
    <div className="space-y-4">
      <EnhancedPatientDetails relation={relation} />
      
      <Card className="w-full overflow-hidden border-t-2 border-primary/70">
        <CardContent className="p-0">
          <ScrollArea className="w-full" type="always">
            <div className={cn(
              "space-y-2",
              isMobile ? "p-1.5 min-w-[400px]" : "p-3 sm:p-4 space-y-4 min-w-[640px]"
            )}>
              {professionalProfile?.company_name && (
                <div className="bg-primary/10 rounded-md p-1 text-xs font-medium text-center">
                  {professionalProfile.company_name}
                </div>
              )}

              <section className="space-y-1">
                <div className="flex items-center gap-1 pb-1 border-b">
                  <MessageSquare className="h-3.5 w-3.5 text-primary" />
                  <h4 className="text-sm font-semibold">Messages récents</h4>
                </div>
                
                {isLoadingMessages ? (
                  <div className="space-y-1">
                    <div className="h-10 animate-pulse bg-muted rounded-md" />
                    <div className="h-10 animate-pulse bg-muted rounded-md" />
                  </div>
                ) : messages && messages.length > 0 ? (
                  <ScrollArea className="h-[150px] w-full pr-1">
                    <div className="space-y-1.5">
                      {messages.map((message) => (
                        <Card key={message.id} className="bg-muted/20 hover:bg-muted/30 transition-colors">
                          <CardContent className="p-2">
                            <div className="space-y-1">
                              <div className="flex justify-between items-center text-[10px]">
                                <p className="font-medium">{message.sender?.full_name || "Utilisateur"}</p>
                                <span className="text-[9px] text-muted-foreground">
                                  {format(new Date(message.created_at), "PPp", { locale: fr })}
                                </span>
                              </div>
                              <p className="text-[10px]">{message.content}</p>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => setReplyTo(message.id)}
                                className="h-5 px-1 w-full justify-start hover:bg-muted/30 text-[10px]"
                              >
                                <Reply className="h-2.5 w-2.5 mr-1" />
                                Répondre
                              </Button>
                            </div>
                          </CardContent>
                        </Card>
                      ))}
                    </div>
                  </ScrollArea>
                ) : (
                  <div className="text-[10px] text-center py-2 text-muted-foreground bg-muted/20 rounded-md">
                    Aucun message échangé avec ce patient
                  </div>
                )}
              </section>
            </div>
            <ScrollBar orientation="horizontal" />
          </ScrollArea>

          {replyTo && (
            <MessageDialog
              open={!!replyTo}
              onOpenChange={() => setReplyTo(null)}
              professionalId={relation.patient?.id || ""}
              professionalName={relation.patient?.full_name || "Patient"}
            />
          )}
        </CardContent>
      </Card>
    </div>
  );
}
